import { isMappingSectionLink, MappingSection, MappingSectionLink } from '../components/mapping/mappingSections';

/**
 * The `?tab=` keys of an integration's detail page and which one a URL lands on.
 *
 * A Shopify integration's mapping lives on one Mapping tab (MOT-278, design "Mapping tab v3") with a
 * left section menu; the open section is `?section=`. MOT-252 had split it into three tabs, and links
 * to those are still in notifications, emails, bookmarks and support replies, so each is an alias:
 *
 * - `?tab=classification[&section=x]` → `?tab=mapping[&section=x]` (its jump links are sections now)
 * - `?tab=matching` → `?tab=mapping&section=listing-matches`
 * - `?tab=metafields` → `?tab=mapping&section=spec-metafields`
 *
 * Lightspeed keeps its own Mapping tab under the same `mapping` key.
 *
 * Every platform's Sync Behavior tab is `?tab=sync-behavior` (MOT-283). It was the Settings tab,
 * `?tab=settings`, which stays an alias on every platform and is rewritten in the URL.
 */
export const DETAIL_TABS = [
  'overview',
  'mapping',
  'classification',
  'matching',
  'metafields',
  'credentials',
  'storefront',
  'fields',
  'catalog',
  'history',
  'sync-behavior',
  'settings',
] as const;

export type DetailTab = (typeof DETAIL_TABS)[number];

/** The MOT-252 Shopify tabs, now sections of the Mapping tab, and the section each one opens. */
export type LegacyShopifyMappingTab = 'classification' | 'matching' | 'metafields';

export const LEGACY_SHOPIFY_MAPPING_TABS: Readonly<Record<LegacyShopifyMappingTab, MappingSection | null>> = {
  classification: null,
  matching: 'listing-matches',
  metafields: 'spec-metafields',
};

export const isLegacyShopifyMappingTab = (tab: string): tab is LegacyShopifyMappingTab =>
  Object.prototype.hasOwnProperty.call(LEGACY_SHOPIFY_MAPPING_TABS, tab);

export interface DetailTabContext {
  isShopify: boolean;
  /** Lightspeed with `lightspeed_store_config`: its own Product Fields and Mapping tabs. */
  showLightspeedConfig: boolean;
  /** Lightspeed with `lightspeed_link_only`: the Catalog match tab. */
  showCatalogMatch: boolean;
}

/**
 * The tab a requested key renders. On Shopify the MOT-252 tabs become Mapping; a tab the platform
 * does not have falls back to `overview`.
 */
export function resolveDetailTab(tab: DetailTab, ctx: DetailTabContext): DetailTab {
  if (tab === 'settings') return 'sync-behavior';
  if (ctx.isShopify && isLegacyShopifyMappingTab(tab)) return 'mapping';
  if (!ctx.isShopify && (tab === 'storefront' || isLegacyShopifyMappingTab(tab))) return 'overview';
  if (!ctx.isShopify && !ctx.showLightspeedConfig && (tab === 'fields' || tab === 'mapping')) return 'overview';
  if (tab === 'catalog' && !ctx.showCatalogMatch) return 'overview';
  return tab;
}

/**
 * The URL rewrite for a link to the old Settings tab (`?tab=settings`), on any platform: the Sync
 * behavior tab. Null when the link needs no rewrite.
 */
export function legacySettingsTabRedirect(tab: string): 'sync-behavior' | null {
  return tab === 'settings' ? 'sync-behavior' : null;
}

/**
 * The URL rewrite for a Shopify link to a MOT-252 tab: Mapping, on the section that tab showed. A
 * section the link already names wins (`?tab=classification&section=name-rules`); an unknown one is
 * dropped. Null when the link needs no rewrite.
 */
export function legacyShopifyMappingRedirect(
  tab: string,
  rawSection: string | null,
): { tab: 'mapping'; section: MappingSectionLink | null } | null {
  if (!isLegacyShopifyMappingTab(tab)) return null;
  const linked = isMappingSectionLink(rawSection) ? rawSection : null;
  return { tab: 'mapping', section: linked ?? LEGACY_SHOPIFY_MAPPING_TABS[tab] };
}

/**
 * The Mapping section a URL opens, worked out on the render that reads it: a MOT-252 alias
 * (`?tab=matching`, `?tab=metafields`) opens its section straight away instead of showing Field
 * ownership for one paint until the URL rewrite lands. Same mapping as `legacyShopifyMappingRedirect`.
 */
export function mappingSectionFromUrl(tab: string, rawSection: string | null): MappingSectionLink | null {
  const redirect = legacyShopifyMappingRedirect(tab, rawSection);
  if (redirect) return redirect.section;
  return isMappingSectionLink(rawSection) ? rawSection : null;
}

/**
 * What the Mapping tab's badge (listing matches to confirm) and caution dot (Motzu Classifications on
 * Unmatched Product Rules) say, in words, for screen readers. Undefined when neither shows.
 */
export function mappingTabAttention(
  t: (key: string, options?: Record<string, unknown>) => string,
  counts: { listingMatches: number; unmatchedCategories: number },
): string | undefined {
  const parts: string[] = [];
  if (counts.listingMatches > 0) {
    parts.push(t('integrations.shopifyDetail.tabs.mappingMatches', { count: counts.listingMatches }));
  }
  if (counts.unmatchedCategories > 0) {
    parts.push(t('integrations.shopifyDetail.tabs.mappingUnmatched', { count: counts.unmatchedCategories }));
  }
  return parts.length > 0 ? `, ${parts.join(', ')}` : undefined;
}

/** In-app link to one section of a Shopify integration's Mapping tab. */
export const shopifyMappingHref = (integrationId: string, section: MappingSection): string =>
  `/app/integrations/${integrationId}?tab=mapping&section=${section}`;
